import { api, upload } from "@/api/client";
import type { AttachmentOut, CommentOut, CommentParentType } from "@/api/types";
import type { ReadMark } from "@/lib/unread";

export interface ChatMessage extends CommentOut {
  author_name: string;
  context_title: string;
  context_url: string;
  reply_to_id: string | null;
  via_token_id: string | null;
  reply_to: { id: string; author_name: string; body_md: string; summary_md?: string | null } | null;
  attachments: (AttachmentOut & {
    transcript: string | null;
    transcription_status: "none" | "pending" | "done" | "failed";
  })[];
}
/** The shared workspace chat, as the server names it (models/comment.py). */
export const WORKSPACE_CHAT_ID = "00000000-0000-0000-0000-00000000cafe";
export const GENERAL_CHAT: ChatContext = { parent_type: "chat", parent_id: WORKSPACE_CHAT_ID };

export interface ChatContext { parent_type?: CommentParentType; parent_id?: string }
/** Spec 033: the message asks one person to answer or act; it waits in their «Решить». */
export interface AskDraft {
  addressee_id: string;
  action: string;
  mode: "agent_ok" | "personal";
  blocking: boolean;
  blocking_note?: string;
}
export interface ChatDraft extends ChatContext { body_md: string; reply_to_id?: string; ask?: AskDraft }
/** Task a60d4d9a: new messages for me by discussion («task:<id>», «chat:<id>»), and in all. */
export interface ChatThreadPage {
  general: ChatMessage | null;
  threads: ChatMessage[];
  next_before: string | null;
  unread?: Record<string, number>;
  unread_total?: number;
}
export const Chat = {
  list: (params: ChatContext & { before?: string; q?: string; limit?: number }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => { if (value) query.set(key, String(value)); });
    // In one discussion the page says where I stopped reading (task a60d4d9a).
    return api<{ messages: ChatMessage[]; next_before: string | null; read_mark?: ReadMark | null }>(`/api/v1/chat/messages?${query}`);
  },
  /** Every discussion by its latest message, freshest first; the shared chat apart, on the first page. */
  threads: (before?: string, limit?: number) => {
    const query = new URLSearchParams();
    if (before) query.set("before", before);
    if (limit) query.set("limit", String(limit));
    const rest = query.toString();
    return api<ChatThreadPage>(`/api/v1/chat/threads${rest ? `?${rest}` : ""}`);
  },
  /** «Прочитано до» this message in its discussion — mine only, never back (task a60d4d9a). */
  read: (messageId: string) =>
    api<ReadMark & { moved: boolean }>("/api/v1/chat/threads/read", { method: "POST", body: { message_id: messageId } }),
  get: (id: string) => api<ChatMessage>(`/api/v1/chat/messages/${id}`),
  send: (body: ChatDraft, key: string) => api<ChatMessage>("/api/v1/chat/messages", {
    method: "POST", body, idempotent: key,
  }),
  media: (body: ChatDraft, files: File[], key: string) => {
    const form = new FormData();
    // A request goes with words only (Spec 033): the form has no place for `ask`.
    Object.entries(body).forEach(([name, value]) => { if (value && typeof value === "string") form.append(name, value); });
    files.forEach((file) => form.append("files", file, file.name));
    return upload<ChatMessage>("/api/v1/chat/messages/media", form, undefined, key);
  },
  transcribe: (id: string) => api(`/api/v1/attachments/${id}/transcribe`, { method: "POST", idempotent: true }),
};
